import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createLanguage,
  updateLanguage,
  fetchLanguages,
} from "../../redux/slices/languageSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../components/ui/use-toast";

const LanguageDialog = ({ open, onClose, language }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    native_name: "",
    direction: "ltr",
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    if (language) {
      setFormData({
        code: language.code || "",
        name: language.name || "",
        native_name: language.native_name || "",
        direction: language.direction || "ltr",
        is_active: language.is_active ?? true,
        sort_order: language.sort_order || 0,
      });
    } else {
      setFormData({
        code: "",
        name: "",
        native_name: "",
        direction: "ltr",
        is_active: true,
        sort_order: 0,
      });
    }
  }, [language, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.code.trim() || formData.code.length > 10) {
      toast({
        title: "Error",
        description: "Please enter a valid language code (max 10 characters)",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter language name",
        variant: "destructive",
      });
      return;
    }

    if (!formData.native_name.trim()) {
      toast({
        title: "Error",
        description: "Please enter native name",
        variant: "destructive",
      });
      return;
    }

    try {
      if (language) {
        await dispatch(
          updateLanguage({ id: language.id, data: formData })
        ).unwrap();
        toast({
          title: "Success",
          description: "Language updated successfully",
        });
      } else {
        await dispatch(createLanguage(formData)).unwrap();
        toast({
          title: "Success",
          description: "Language created successfully",
        });
      }
      dispatch(fetchLanguages());
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to save language",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {language ? "Edit Language" : "Add New Language"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Language Code */}
          <div className="space-y-2">
            <Label>Language Code *</Label>
            <Input
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toLowerCase() })
              }
              placeholder="e.g., en, ar, ur, fr"
              maxLength={10}
              disabled={!!language}
            />
            <p className="text-xs text-muted-foreground">
              ISO 639-1 code (cannot be changed after creation)
            </p>
          </div>

          {/* Language Name */}
          <div className="space-y-2">
            <Label>Name (English) *</Label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., English, Arabic, Urdu"
            />
          </div>

          {/* Native Name */}
          <div className="space-y-2">
            <Label>Native Name *</Label>
            <Input
              value={formData.native_name}
              onChange={(e) =>
                setFormData({ ...formData, native_name: e.target.value })
              }
              placeholder="e.g., English, العربية, اردو"
              dir="auto"
            />
            <p className="text-xs text-muted-foreground">
              How the language name appears in its own script
            </p>
          </div>

          {/* Direction */}
          <div className="space-y-2">
            <Label>Text Direction *</Label>
            <Select
              value={formData.direction}
              onValueChange={(value) =>
                setFormData({ ...formData, direction: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ltr">Left-to-Right (LTR)</SelectItem>
                <SelectItem value="rtl">Right-to-Left (RTL)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Order */}
          <div className="space-y-2">
            <Label>Sort Order</Label>
            <Input
              type="number"
              value={formData.sort_order}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sort_order: parseInt(e.target.value) || 0,
                })
              }
              min="0"
            />
            <p className="text-xs text-muted-foreground">
              Display order in language selection (lower numbers appear first)
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Active Status</Label>
              <p className="text-xs text-muted-foreground">
                Make this language available for selection
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_active: checked })
              }
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {language ? "Update Language" : "Create Language"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageDialog;
